import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { getErrorMessage } from '@/utils/errorMessage';
import { EmployeeService } from '../employee.service';
import { logoutBeforeLoginIfNeeded } from '@/utils/logoutBeforeLogin';

/** Login CPF + companyId (`/empresa/formulario/:companyId`). */
export const useLoginEmployee = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (body: Employees.Login) => {
			await logoutBeforeLoginIfNeeded(queryClient);
			return EmployeeService.login(body);
		},
	});
};

export const useCreateEmployee = (
	onSuccess?: (employee: Employees.Employee) => void,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (body: Employees.Create) => EmployeeService.create(body),
		onSuccess: (employee) => {
			queryClient.invalidateQueries({ queryKey: ['employees'] });
			queryClient.invalidateQueries({
				queryKey: ['company', employee.company_id],
			});
			queryClient.invalidateQueries({ queryKey: ['companies'] });
			toast.success('Funcionário criado com sucesso!');
			if (onSuccess) onSuccess(employee);
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(error, 'Erro na criação do funcionário'),
			);
		},
	});
};

export const useBulkCreateEmployees = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (body: Employees.BulkCreate) =>
			EmployeeService.bulkCreate(body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['employees'] });
			toast.success('Funcionários criados com sucesso!');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro no cadastro em lote'));
		},
	});
};

export const usePatchEmployee = (
	onSuccess?: (employee: Employees.Employee) => void,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, body }: { id: string; body: Employees.Update }) =>
			EmployeeService.patch(id, body),
		onSuccess: (employee) => {
			queryClient.invalidateQueries({ queryKey: ['employees'] });
			queryClient.invalidateQueries({
				queryKey: ['company', employee.company_id],
			});
			queryClient.invalidateQueries({ queryKey: ['companies'] });
			toast.success('Funcionário atualizado com sucesso!');
			if (onSuccess) onSuccess(employee);
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(error, 'Erro na atualização do funcionário'),
			);
		},
	});
};

export const usePatchEmployeeOrganizationLink = (
	onSuccess?: (employee: Employees.Employee) => void,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			id,
			body,
		}: {
			id: string;
			body: Employees.PatchOrganizationLinkBody;
		}) => EmployeeService.patchOrganizationLink(id, body),
		onSuccess: (employee) => {
			queryClient.invalidateQueries({ queryKey: ['employees'] });
			queryClient.invalidateQueries({
				queryKey: ['company', employee.company_id],
			});
			queryClient.invalidateQueries({ queryKey: ['companies'] });
			toast.success('Vínculo com departamento/setor atualizado!');
			if (onSuccess) onSuccess(employee);
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(
					error,
					'Erro ao atualizar vínculo com departamento/setor',
				),
			);
		},
	});
};

export const useRemoveEmployee = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => EmployeeService.remove(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['employees'] });
			queryClient.invalidateQueries({ queryKey: ['company'] });
			queryClient.invalidateQueries({ queryKey: ['companies'] });
			toast.success('Funcionário removido com sucesso!');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao remover funcionário'));
		},
	});
};

export const useUpdateEmployeeStatus = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, active }: { id: string; active: boolean }) =>
			EmployeeService.updateStatus(id, { active }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['employees'] });
			toast.success('Status do funcionário atualizado!');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao atualizar status'));
		},
	});
};

export const useLinkEmployeeToUser = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, userId }: { id: string; userId: string }) =>
			EmployeeService.linkToUser(id, userId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['employees'] });
			toast.success('Funcionário vinculado ao usuário!');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao vincular usuário'));
		},
	});
};

export const useUnlinkEmployeeFromUser = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => EmployeeService.unlinkFromUser(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['employees'] });
			toast.success('Vínculo removido com sucesso!');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao remover vínculo'));
		},
	});
};
